/**
 * @lines98/client/lib/reward-block — блок добычи меты «Ювелир» на экране итогов.
 *
 * ЗАЧЕМ / WHY THIS EXISTS (meta-jeweler-surfaces.md §4.1, owner-design 2026-06-05;
 * Срез 1b). После server-scored партии (доска дня / не-daily) показываем, ЧТО
 * добыто этой партией. Принцип owner'а: **визуал каждой валюты повторяет её
 * математику** —
 *   · золото от очков (непрерывно) → ШКАЛА «переплавки очков» + вылет монеты;
 *   · осколки от клиров (дискретно) → ЛЕНТА клиров + счётчик-пульки (НЕ шкала);
 *   · камень (редко) → ЦЕРЕМОНИЯ (опускается в лоток радуги / перелив в осколки);
 *   · артефакты реагируют ИСКРОЙ, когда их per-tick шанс сыграл (в Срезе 1 артефактов
 *     нет → путь инертен; hook заложен, не хардкодим наличие).
 *
 * Модель «ствол → ветки» (owner-directive 2026-05-30): это переиспользуемый МОДУЛЬ,
 * НЕ верстается в result-screen. Экран монтирует его тонкой веткой. Микро-визуалы
 * (шкала / лента / церемония) — приватные под-объекты этого модуля (single-source).
 *
 * Server-truth (AC-12, I-1 p2w-стена): блок ВИЗУАЛИЗИРУЕТ `MetaFaucetSummary` с
 * сервера — клиент НЕ пересчитывает добычу и НЕ кормит ею очки/лидерборды. Числа
 * приходят готовыми; модуль их только раскладывает по анимациям.
 *
 * Анимации — ТОЛЬКО transform/opacity (ui-performance §1; никаких infinite
 * box-shadow/filter); `prefers-reduced-motion: reduce` → мгновенный показ итога.
 * Плавность движения монеты/камня — smooth-motion §2 (translate/opacity, GPU).
 * Тайминги анимации — ТОКЕНЫ `--reward-*` (reward-block.css `.reward-block`), один
 * источник (item 9 owner-playtest 2026-06-06: «слишком быстро → замедлить»).
 *
 * РАЗБИВКА ИСТОЧНИКОВ (item 5 owner-playtest 2026-06-06 «откуда столько золота/осколков»):
 * блок показывает, ИЗ ЧЕГО сложился итог — золото: база (floor(score/threshold)) +
 * множитель доски дня (`DAILY_GOLD_MULTIPLIER`) + «прочие бонусы» (остаток =
 * per-tick артефакт-бонусы); осколки: от клиров + перелив камней; ачивки: «+N зол./оск.»
 * на строке ачивки (achievementReward из каталога). Всё ВЫВОДИМО на клиенте из
 * server-truth + config — НЕ выдумано (no-hardcode §4); сумма строк === итог.
 *
 * ⚠️ ESCALATION (item 5, owner-playtest 2026-06-06): `MetaFaucetSummary.gold_earned`
 * НЕ выделяет вклад артефактов отдельным полем (он в `meta_ledger.detail` сервера, не
 * в сводке). Поэтому артефакт-часть выводим как ОСТАТОК `gold_earned − база·множитель`
 * и честно подписываем «прочие бонусы» (НЕ «от артефактов» — точный состав неизвестен).
 * В Срезе 1 артефактов нет → остаток 0 → строка не показывается, разбивка точна. Когда
 * появятся артефакты (Срез 2), для точной строки «от артефактов» серверу нужно добавить
 * `gold_from_artifacts` в `MetaFaucetSummary` (micro-серверная задача) — до тех пор
 * «прочие бонусы» честно замыкает сумму. Цифры НЕ подгоняются под итог.
 *
 * SCOPE (Срез 1b/3b): шкала золота + лента/счётчик осколков + церемония камня
 * (Срез 3b — отражает РЕАЛЬНОЕ размещение из сводки: `gems_closed` закрыли цвет
 * активной радуги ИЛИ `gems_overflow` перелились в осколки, не плейсхолдер «перелив
 * всегда») + инертный artifact-spark hook. **HUD текущих балансов УБРАН из блока
 * (owner-decision 2026-06-07): он стоял в строке с «Добыча партии» и сбивал с фактической
 * добычей + раздувал окно; текущие балансы — на Мастерской/Профиле (§3.1).** NOT IN SCOPE:
 * уровень/XP/ачивки (Срез 4), интерактивный лоток радуги (Мастерская — rainbow-tray),
 * реакция конкретных артефактов (Срез 2 — изделия), HUD текущих балансов (см. выше).
 *
 * Cross-references:
 *   - meta-jeweler-surfaces.md §4.1 (детальный концепт). HUD §3 — НЕ в этом блоке (owner 2026-06-07).
 *   - meta-jeweler.md §2.1/§2.2/§2.3 (модель тиков золота/осколков/камня).
 *   - @lines98/shared/types/game (MetaFaucetSummary) + config/meta (пороги).
 *   - components/meta-icons (глифы).
 *   - .claude/skills/{smooth-motion-animation,ui-performance-and-assets} (анимации).
 */

import './reward-block.css';

import {
  DAILY_GOLD_MULTIPLIER,
  GOLD_PER_POINT_THRESHOLD,
} from '@lines98/shared/config/meta';
import type {
  ColorIndex,
  GameMode,
  MetaFaucetSummary,
  MetaProgressionSummary,
} from '@lines98/shared/types/game';

import { levelBadgeHtml } from '../components/level-badge.js';
import { ICON_GOLD, ICON_SHARD, gemGlyph } from '../components/meta-icons.js';
import { formatRuNumber } from './format.js';
import { t } from './i18n/index.js';

export interface RewardBlockOptions {
  /** Серверная сводка добычи за партию (server-truth; §4.1 must-visible). */
  readonly summary: MetaFaucetSummary;
  /**
   * Режим партии — задаёт ёмкость шкалы золота («переплавка очков»: каждые
   * `GOLD_PER_POINT_THRESHOLD[mode]` очков = 1 монета). Доска дня = predict-фикс.
   */
  readonly mode: GameMode;
  /**
   * Итоговый счёт партии (для визуализации шкалы «очки → золото»). Server-verified,
   * приходит из submit-результата. Чисто для анимации шкалы — не пересчёт добычи.
   */
  readonly score: number;
  /**
   * Партия — зачёт доски дня? Нужно для РАЗБИВКИ источников золота (item 5,
   * owner-playtest 2026-06-06): доска дня применяет множитель `DAILY_GOLD_MULTIPLIER`
   * к базе. Чисто для подписи строк разбивки (база/множитель) — не пересчёт добычи
   * (`gold_earned` уже server-truth). Default `false` (casual/all_time без множителя).
   */
  readonly isDaily?: boolean;
  /**
   * Запускать вступительную анимацию (шкала заливается, монеты вылетают, камень
   * опускается). `false` → мгновенный итог (для prefers-reduced-motion / тестов).
   * Default: уважает `prefers-reduced-motion`.
   */
  readonly animate?: boolean;
}

export interface RewardBlockHandle {
  /** Корневой элемент блока — потребитель уже получил его смонтированным в host. */
  readonly element: HTMLElement;
  /** Размонтировать: остановить анимации, снять DOM. */
  unmount(): void;
}

/** Учитывать ли prefers-reduced-motion (без window — тесты — считаем «да, reduce»). */
function prefersReducedMotion(): boolean {
  if (typeof window === 'undefined' || !window.matchMedia) return false;
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

/**
 * Смонтировать блок добычи в `host`. Возвращает handle. Числа берутся из `summary`
 * (server-truth) — модуль их только визуализирует.
 */
export function mountRewardBlock(host: HTMLElement, opts: RewardBlockOptions): RewardBlockHandle {
  const { summary, mode, score } = opts;
  const isDaily = opts.isDaily ?? false;
  const animate = (opts.animate ?? !prefersReducedMotion()) && !prefersReducedMotion();

  const root = document.createElement('section');
  root.className = 'reward-block';
  root.setAttribute('aria-label', t('game.reward.blockAria'));

  // --- Заголовок (ТОЛЬКО фактическая добыча партии). HUD текущих балансов УБРАН
  // (owner-decision 2026-06-07): он стоял в одной строке с «Добыча партии» и сбивал —
  // текущий остаток (напр. 8 осколков) читался как добыча партии — + раздувал окно.
  // Текущие балансы видны на мета-поверхностях (Мастерская / Профиль, §3.1); здесь —
  // только то, что добыто ЭТОЙ партией. ---
  const header = document.createElement('div');
  header.className = 'reward-block-header';
  const title = document.createElement('h2');
  title.className = 'reward-block-title';
  title.textContent = t('game.reward.blockTitle');
  header.append(title);
  root.appendChild(header);

  // --- Тело: золото-шкала + осколки-лента + (опц.) камень-церемония ---
  const body = document.createElement('div');
  body.className = 'reward-block-body';
  root.appendChild(body);

  // Все строки строятся СРАЗУ С ФИНАЛЬНЫМ КОНТЕНТОМ (числа/лейблы) — анимация лишь
  // CSS-слой поверх готового контента. Так контент существует независимо от
  // тайминга анимации (скриншот посреди реверала всё равно несёт суммы), а
  // `prefers-reduced-motion` / Playwright `animations:'disabled'` корректно дают
  // финальный вид (CSS-анимация фаст-форвардится к концу). Никакого JS-setTimeout-
  // gated текста (ui-performance + детерминизм тестов).
  body.appendChild(buildGoldRow(summary.gold_earned, score, mode, isDaily));
  body.appendChild(buildShardRow(summary));
  // Камень-церемония — ТОЛЬКО если выпал камень (§4.1 «нет камня → блок свёрнут»).
  if (summary.gems_dropped > 0) {
    body.appendChild(buildGemRow(summary));
  }

  // --- Прогрессия аккаунта (Срез 4b, §4.1 «уровень/ачивки — в конце окна»). ---
  // Из server-truth `summary.progression` (level-up'ы + новые ачивки + веха-награды).
  // Показываем ТОЛЬКО когда есть что показать (xp/level-up/ачивка), иначе блок не растёт.
  if (summary.progression) {
    const progressionBlock = buildProgressionBlock(summary.progression);
    if (progressionBlock) root.appendChild(progressionBlock);
  }

  // --- Открытие помощника БОЛЬШЕ НЕ живёт в reward-block (редизайн 2026-06-20,
  // owner-playtest «приходится скроллить»): уведомление переехало в МОДАЛЬНЫЙ ОВЕРЛЕЙ поверх
  // доски (lib/helper-unlock-overlay), который монтирует result-screen. reward-block больше
  // НЕ читает gnome_unlocks / НЕ зовёт revealGnomes / НЕ строит секцию helper-unlock. ---

  host.appendChild(root);

  // Анимация вступления — ЧИСТО CSS (staggered fade-in строк через animation-delay,
  // gated классом `--animating`). prefers-reduced-motion → CSS гасит анимацию (строки
  // видны сразу, см. reward-block.css). Контент уже на месте, JS лишь ставит флаг.
  if (animate) {
    root.classList.add('reward-block--animating');
  }

  return {
    element: root,
    unmount(): void {
      if (root.parentNode) root.parentNode.removeChild(root);
    },
  };
}

// =============================================================================
// Золото — шкала «переплавки очков» (§4.1)
// =============================================================================

/**
 * Строка золота: шкала ёмкостью = порог режима; очки «переплавляются» в монеты
 * (1000 очков в Предсказании = 10 монет при пороге 100). Шкала — визуальная
 * метафора непрерывного крана (§4.1); число монет = server-truth `gold_earned`
 * (НЕ пересчёт). Artifact-spark hook заложен (Срез 1 — инертен). Контент финальный
 * сразу; CSS-reveal лишь проявляет строку.
 *
 * ШКАЛА ЗАПОЛНЯЕТСЯ ПРОПОРЦИОНАЛЬНО (owner-playtest 2026-06-06 item 1: «полоска
 * всегда полная»). Шкала — это ПРОГРЕСС К СЛЕДУЮЩЕЙ МОНЕТЕ: доля `(score mod
 * threshold) / threshold` (остаток очков, не дотянувший до ещё одной монеты).
 * 1000 очков при пороге 100 = ровно 10 монет, остаток 0 → шкала пустая (всё
 * переплавлено в монеты). 1050 → 10 монет + полшкалы. Чисто визуальная доля —
 * `gold_earned` (число монет) остаётся server-truth, шкалу НЕ кормит. Заполнение
 * пишем CSS-переменной `--gold-fill-frac` на fill (CSS scaleX к этой доле,
 * composite-only). До-JS / reduced-motion → CSS сразу ставит финальную долю.
 */
function buildGoldRow(
  goldEarned: number,
  score: number,
  mode: GameMode,
  isDaily: boolean,
): HTMLElement {
  // threshold — ёмкость шкалы (очков на одну монету) + подпись «сколько очков на монету».
  const threshold = GOLD_PER_POINT_THRESHOLD[mode];

  // Доля заполнения = остаток очков, не дотянувший до следующей монеты, к ёмкости.
  // clamp [0,1] защитно (на случай странных входов / нулевого порога). Когда очки
  // кратны порогу — доля 0 (всё переплавлено), шкала пустая, а не «всегда полная».
  const remainder = threshold > 0 ? score % threshold : 0;
  const fillFraction = threshold > 0 ? Math.min(1, Math.max(0, remainder / threshold)) : 0;

  const root = document.createElement('div');
  root.className = 'reward-row reward-row--gold';

  const icon = document.createElement('span');
  icon.className = 'reward-row-icon';
  icon.setAttribute('aria-hidden', 'true');
  icon.innerHTML = ICON_GOLD;

  const label = document.createElement('span');
  label.className = 'reward-row-label';
  label.textContent = t('game.reward.gold');

  // Шкала «переплавки» — заполняемая полоса (заливка scaleX — CSS, composite-only).
  const meter = document.createElement('div');
  meter.className = 'reward-gold-meter';
  meter.setAttribute('role', 'presentation');
  const fill = document.createElement('div');
  fill.className = 'reward-gold-meter-fill';
  // Пропорция заполнения → CSS-переменная (CSS использует её как target scaleX).
  fill.style.setProperty('--gold-fill-frac', String(fillFraction));
  meter.appendChild(fill);

  // Artifact-spark hook — точка под искру, когда per-tick шанс сыграл (Срез 1: нет
  // артефактов → элемент есть, но не активируется; будущий Срез 2 включит .spark).
  const spark = document.createElement('span');
  spark.className = 'reward-artifact-spark';
  spark.setAttribute('aria-hidden', 'true');
  meter.appendChild(spark);

  const amount = document.createElement('span');
  amount.className = 'reward-row-amount';
  amount.textContent = `+${formatRuNumber(goldEarned)}`;
  amount.setAttribute(
    'aria-label',
    t('game.reward.goldBreakdownLine', {
      score: formatRuNumber(score),
      threshold: formatRuNumber(threshold),
      gold: formatRuNumber(goldEarned),
    }),
  );

  root.append(icon, label, meter, amount);

  // Разбивка источников золота (item 5: «непонятно, откуда столько золота»). Все
  // строки ВЫВОДИМЫ НА КЛИЕНТЕ из server-truth + config — НЕ выдуманы (no-hardcode §4):
  //   · база      = floor(score / threshold) монет (continuous-кран, meta §2.1);
  //   · ×доска дня = база·(DAILY_GOLD_MULTIPLIER − 1) (только daily; meta §2.1);
  //   · прочие    = gold_earned − (база·множитель) = per-tick артефакт-бонусы (Срез 1
  //                 артефактов нет → 0). ЯВНО подписана «прочие бонусы» — НЕ подгоняем
  //                 цифры под итог (см. escalation `gold_from_artifacts` в шапке +
  //                 SESSIONS). Сумма строк === gold_earned по построению (остаток honest).
  const breakdown = buildGoldBreakdown(goldEarned, score, threshold, isDaily);
  if (breakdown) root.appendChild(breakdown);
  return root;
}

/**
 * Под-строка разбивки золота (item 5). Возвращает `null`, когда разбивка тривиальна
 * (нет множителя И нет прочих бонусов — т.е. casual без артефактов: база == итог,
 * пояснять нечего). Иначе — компактный перечень: база / ×доска дня / прочие бонусы.
 *
 * ЧЕСТНОСТЬ (item 5 escalation): `gold_earned` (server-truth) = база·множитель +
 * per-tick артефакт-бонусы. Сервер НЕ шлёт `gold_from_artifacts` в сводке (только
 * `meta_ledger.detail`), поэтому артефакт-часть выводим как ОСТАТОК `gold_earned −
 * база·множитель` и подписываем «прочие бонусы» (не «от артефактов» — мы не знаем
 * точный состав). В Срезе 1 артефактов нет → остаток 0 → строка «прочие» не
 * показывается. Сумма строк ВСЕГДА равна итогу (остаток замыкает).
 */
function buildGoldBreakdown(
  goldEarned: number,
  score: number,
  threshold: number,
  isDaily: boolean,
): HTMLElement | null {
  const baseCoins = threshold > 0 ? Math.floor(score / threshold) : 0;
  const multiplier = isDaily ? DAILY_GOLD_MULTIPLIER : 1;
  const goldFromMultiplier = baseCoins * (multiplier - 1);
  // Остаток = всё, что не объясняется базой·множителем (per-tick артефакты; Срез 1 = 0).
  // max(0,…) защитно — server-truth итог — авторитет, отрицательного быть не должно.
  const goldOther = Math.max(0, goldEarned - baseCoins * multiplier);

  // Тривиальный случай: нет множителя И нет прочих → база == итог, разбивка не нужна.
  if (multiplier === 1 && goldOther === 0) return null;

  const rows: Array<{ label: string; value: number }> = [
    { label: t('game.reward.goldBreakdownBase'), value: baseCoins },
  ];
  if (goldFromMultiplier > 0) {
    rows.push({ label: t('game.reward.goldBreakdownDaily', { n: multiplier }), value: goldFromMultiplier });
  }
  if (goldOther > 0) {
    rows.push({ label: t('game.reward.goldBreakdownOther'), value: goldOther });
  }
  return buildBreakdownList(rows);
}

/**
 * Общий рендер списка разбивки (источник: метка + «+N»). Переиспользуется золотом и
 * осколками (single-source — не копия по валютам). Полная ширина строки добычи
 * (grid-column 1/-1), мелкий текст под основной строкой.
 */
function buildBreakdownList(rows: ReadonlyArray<{ label: string; value: number }>): HTMLElement {
  const list = document.createElement('div');
  list.className = 'reward-breakdown';
  list.setAttribute('aria-hidden', 'true'); // основная строка несёт aria-label итога
  for (const r of rows) {
    const item = document.createElement('span');
    item.className = 'reward-breakdown-item';
    const lab = document.createElement('span');
    lab.className = 'reward-breakdown-label';
    lab.textContent = r.label;
    const val = document.createElement('span');
    val.className = 'reward-breakdown-value';
    val.textContent = `+${formatRuNumber(r.value)}`;
    item.append(lab, val);
    list.appendChild(item);
  }
  return list;
}

// =============================================================================
// Осколки — лента «жатвы клиров» + счётчик-пульки (§4.1)
// =============================================================================

/**
 * Строка осколков: ДИСКРЕТНЫЙ счётчик (НЕ шкала — §4.1 «осколки от клиров → пульки»).
 * Server-truth `shards_earned` (вкл. `shards_from_overflow` от перелива камней).
 * Если осколков нет — подсказка «собери линию 9+» (§4.1). Контент финальный сразу.
 */
function buildShardRow(summary: MetaFaucetSummary): HTMLElement {
  const root = document.createElement('div');
  root.className = 'reward-row reward-row--shards';

  const icon = document.createElement('span');
  icon.className = 'reward-row-icon';
  icon.setAttribute('aria-hidden', 'true');
  icon.innerHTML = ICON_SHARD;

  const label = document.createElement('span');
  label.className = 'reward-row-label';
  label.textContent = t('game.reward.shards');

  // Лента-полоска клиров (визуальный «след жатвы»). В Срезе 1b у нас нет пер-клир
  // разбивки в summary (только итог) → лента = декоративный strip под счётчиком;
  // структура заложена под будущую пер-клир детализацию (§4.1 «лента клиров»).
  const strip = document.createElement('div');
  strip.className = 'reward-shard-strip';
  strip.setAttribute('aria-hidden', 'true');

  const amount = document.createElement('span');
  amount.className = 'reward-row-amount';

  const hasShards = summary.shards_earned > 0;
  amount.setAttribute(
    'aria-label',
    hasShards
      ? t('game.reward.shardsEarnedAria', { n: formatRuNumber(summary.shards_earned) })
      : t('game.reward.shardsEmptyAria'),
  );

  root.append(icon, label, strip, amount);

  if (hasShards) {
    amount.textContent = `+${formatRuNumber(summary.shards_earned)}`;
    // Камни как ресурс T1: перелив камней → осколки СНЯТ (§2.5). Осколки теперь
    // полностью «от клиров» (база режима + бонус за размер линии) → отдельная
    // строка-разбивка «перелив» больше не нужна.
  } else {
    amount.textContent = '0';
    root.classList.add('reward-row--empty');
    const hint = document.createElement('span');
    hint.className = 'reward-row-hint';
    hint.textContent = t('game.reward.shardsHint');
    root.appendChild(hint);
  }
  return root;
}

// =============================================================================
// Камень — церемония (§4.1). Срез 3a/3b: камень РАЗМЕЩАЕТСЯ серверной истиной —
// `closed` (закрыл ячейку цвета активной радуги) ИЛИ `overflow` (нет активного / цвет
// уже закрыт → перелив в осколки, no-hoard AC-4). Строка отражает РЕАЛЬНОЕ размещение
// из сводки (`gems_closed` / `gems_overflow`), а не плейсхолдер «перелив всегда».
// =============================================================================

/**
 * Строка камня-церемонии: гранёный камень + что с ним стало (server-truth размещение).
 * Три случая по сводке:
 *   - только закрытие (`gems_closed > 0`, `gems_overflow = 0`): «→ закрыл цвет радуги»;
 *   - только перелив (`gems_overflow > 0`, `gems_closed = 0`): «→ перелив +N осколков»;
 *   - смешанное (оба > 0): «→ N в радугу · M в осколки» (часть закрылась, часть перелилась).
 *
 * Цвет камня плейсхолдер-нейтрален: сводка не несёт per-gem цвет (только счётчики
 * размещения); рисуем по colorIndex 1 как репрезентативный камень. Полноценный лоток
 * радуги с цветами — в Мастерской (rainbow-tray).
 */
function buildGemRow(summary: MetaFaucetSummary): HTMLElement {
  const root = document.createElement('div');
  root.className = 'reward-row reward-row--gem';

  const icon = document.createElement('span');
  icon.className = 'reward-row-icon reward-gem-icon';
  icon.setAttribute('aria-hidden', 'true');
  // РЕАЛЬНЫЙ цвет(а) выпавших самоцветов из server-truth `gem_pool_delta`
  // (index = colorIndex 1..N, value = сколько выпало этого цвета). Рисуем глиф
  // КАЖДОГО присутствующего цвета — игрок видит ровно те самоцветы, что собрал.
  // (owner-bug 2026-06-28: была заглушка `gemGlyph(1)`=рубин независимо от факта —
  // собрал сапфир, показывали рубин. Данные сервер слал всегда, клиент игнорировал.)
  // Кратность одного цвета несёт счётчик в подписи (gemLabel); сам цвет — глифом.
  const delta = summary.gem_pool_delta ?? [];
  let gemGlyphs = '';
  for (let color = 1; color < delta.length; color++) {
    if ((delta[color] ?? 0) > 0) gemGlyphs += gemGlyph(color as ColorIndex);
  }
  // Fallback на нейтральный глиф, только если delta пуст при gems_dropped>0 (не должно
  // случаться — сервер всегда шлёт delta; защита от рассинхрона типов).
  icon.innerHTML = gemGlyphs || gemGlyph(1);

  const label = document.createElement('span');
  label.className = 'reward-row-label';
  const gemWord = summary.gems_dropped === 1 ? t('game.reward.gemOne') : t('game.reward.gemMany');
  label.textContent = t('game.reward.gemLabel', {
    n: formatRuNumber(summary.gems_dropped),
    word: gemWord,
  });

  // Камни как ресурс (`meta-gems-resource.md` T1): камень больше НЕ размещается в
  // радугу и не переливается — каждый падает в ПУЛ своего цвета (игрок вручную
  // вставит его в зверя в Мастерской). Строка-flow = «→ в запас». Полноценную
  // per-color визуализацию (`gem_pool_delta`) рисует обновлённый result-screen (T2).
  const flow = document.createElement('span');
  flow.className = 'reward-gem-flow';
  flow.textContent = t('game.reward.gemFlow');
  flow.setAttribute(
    'aria-label',
    t('game.reward.gemFlowAria', { n: formatRuNumber(summary.gems_dropped) }),
  );

  root.append(icon, label, flow);
  return root;
}

// =============================================================================
// Прогрессия аккаунта (Срез 4b, §4.1 «уровень/ачивки — в конце окна»).
// Из server-truth `MetaProgressionSummary` (level-up'ы + веха-награды + новые ачивки).
// Display-only (I-1) — визуализация, не пересчёт. Не назойливо: показываем только то,
// что реально случилось этой партией (XP-прирост / level-up / разблокировки).
// =============================================================================

/**
 * Построить под-блок прогрессии: «+XP», level-up («Уровень N!» с бейджем), веха-награды
 * (слот Книги / пакет), новые ачивки (имя из локали). Возвращает null, если показывать
 * нечего (нет XP, нет level-up, нет ачивок) — чтобы блок добычи не рос пустой строкой.
 */
function buildProgressionBlock(p: MetaProgressionSummary): HTMLElement | null {
  const hasXp = p.xp_earned > 0;
  const hasLevelUp = p.levels_gained > 0;
  const hasMilestones = p.milestone_rewards.length > 0;
  // Ачивки УБРАНЫ из блока добычи (R11b, тикет fe-achievement-result-window, owner-реш.1):
  // разблокированные за партию достижения показываются ОТДЕЛЬНЫМ плавающим окошком над
  // доской (lib/achievement-result-window), НЕ строками под золотом/осколками. XP /
  // level-up / milestone — ОСТАЮТСЯ здесь (owner-реш.1).
  if (!hasXp && !hasLevelUp && !hasMilestones) return null;

  const section = document.createElement('div');
  section.className = 'reward-progression';
  section.setAttribute('aria-label', t('game.reward.progressAria'));

  // «+XP» строка (всегда, если XP начислен — мягкая обратная связь за игру).
  if (hasXp) {
    const xpRow = document.createElement('div');
    xpRow.className = 'reward-prog-row reward-prog-row--xp';
    xpRow.innerHTML = `
      <span class="reward-prog-label">${t('game.reward.xpLabel')}</span>
      <span class="reward-prog-amount">${t('game.reward.xpAmount', { n: formatRuNumber(p.xp_earned) })}</span>
    `;
    section.appendChild(xpRow);
  }

  // Level-up — «Уровень N!» с бейджем нового уровня (визуальный акцент, §2.2 микро-всплеск).
  if (hasLevelUp) {
    const lvlRow = document.createElement('div');
    lvlRow.className = 'reward-prog-row reward-prog-row--levelup';
    lvlRow.innerHTML = `
      ${levelBadgeHtml(p.level_after)}
      <span class="reward-prog-levelup-text">${t('game.reward.levelUp', { n: formatRuNumber(p.level_after) })}</span>
    `;
    lvlRow.setAttribute('aria-label', t('game.reward.levelUpAria', { n: p.level_after }));
    section.appendChild(lvlRow);
  }

  // Веха-награды (слот Книги / пакет) — §2.3.
  for (const m of p.milestone_rewards) {
    const mRow = document.createElement('div');
    mRow.className = 'reward-prog-row reward-prog-row--milestone';
    const label =
      m.kind === 'slot'
        ? t('game.reward.milestoneSlot', { n: formatRuNumber(m.level) })
        : t('game.reward.milestonePack', { n: formatRuNumber(m.level) });
    mRow.textContent = label;
    section.appendChild(mRow);
  }

  // (Строки разблокированных ачивок здесь БОЛЬШЕ НЕ рисуются — R11b. Они уехали в
  // плавающее окошко над доской lib/achievement-result-window; reward-block держит
  // ТОЛЬКО XP / level-up / milestone.)
  return section;
}
